import { useState } from 'react';
import {
  StepCta,
  StepHeading,
  StepSub,
} from '../../OnboardingShell/StepLayout';
import { BarNote } from '../../OnboardingShell/onboardingV2';
import { PARQ_QUESTIONS } from '../../../fixtures/onboarding';
import { parqAnswersComplete } from '../../../lib/parqAnswers';
import type { OnboardingAnswers } from '../../../hooks/onboardingReducer';
import styles from './Step3ParQ.module.css';

type Step3Props = {
  answers: OnboardingAnswers;
  setAnswer: (patch: Partial<OnboardingAnswers>) => void;
  onContinue: () => void;
  /**
   * Round-21 critic F2 / founder copy ruling 2026-09-09 23:01 PT (row 6) —
   * the athlete did not choose this screen: the reveal's dashboard CTA sent
   * her here because her screening is incomplete (B108). One connective
   * sentence says why, and it shows ONLY on that arrival, never on the
   * ordinary forward pass through step 3.
   */
  arrivedFromReveal?: boolean;
};

/**
 * Step 3 — PAR-Q (Physical Activity Readiness Questionnaire).
 *
 * 7 yes/no questions per the existing app's PAR-Q. Continue is
 * disabled until ALL 7 are answered. ANY "yes" reveals an amber
 * warning advising medical clearance.
 *
 * Phase 2 keeps the app's existing 7 questions verbatim.
 * Canonicalization to CSEP 2002 form (add chest-pain-at-rest, merge
 * canonical Q1+Q6) is a separate tech-debt item, NOT in Phase 2.
 *
 * Yes/No selection styling per Claude Design's mockup:
 *   No (selected): signal marine border + signal-soft bg + signal text
 *   Yes (selected): danger border + danger-tinted bg + danger text
 */
/** B2 — month names for the required birth selector. */
const BIRTH_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Round-29 critic 2 — the other half of the gate, said out loud. Continue is
 * `!allDone || !birthDone`; the counting line only ever spoke for the first
 * half, so a screening answered in full with the birth row still empty read
 * "7 of 7 answered" over a Continue that refuses, and nothing on the screen
 * named the missing half. It is a pointer, not a warning: the birth row is
 * eight lines above it.
 */
const BIRTH_REQUIRED_HINT = 'Add your birth month and year to continue.';

/** B465 — the reason beside a disabled Continue while some of the seven are open. */
const QUESTIONS_REQUIRED_HINT = 'Answer all 7 questions to continue.';

export function Step3ParQ({ answers, setAnswer, onContinue, arrivedFromReveal }: Step3Props) {
  const parq = answers.parq ?? {};

  const setAnswerForKey = (key: string, value: boolean) => {
    setAnswer({ parq: { ...parq, [key]: value } });
  };

  // B116 — ONE rule, imported, never restated. `parq[q.key] !== undefined`
  // let a hydrated blob whose canonical keys hold non-booleans (a `null`, the
  // string 'false', a legacy camelCase bag) enable Continue with no Yes or No
  // showing; Step 7 and the completion payload then refused it on
  // `parqAnswersComplete` and bounced her back here, forever, with nothing
  // said. Step 3, Step 7 and `usePersistOnboarding` now decide "screened" with
  // the same predicate (`lib/parqAnswers.ts`), so the loop has no source.
  const allDone = parqAnswersComplete(answers.parq);
  // Build 32 item 7 (health caution placement) — one card, directly under the
  // TOPMOST question answered yes; more yeses add no cards. `key="parq-caution"`
  // is stable across renders so a later yes moving the card up or down does
  // not unmount + remount it (role="alert" would announce twice).
  const firstYesKey = PARQ_QUESTIONS.find((q) => parq[q.key] === true)?.key;
  // Round-21 UX 1 — a dimmed Continue was the only signal that something was
  // missing, and on a long screen the blank row can sit far above the fold.
  // The count reads the SAME rule the gate reads (an explicit boolean on a
  // canonical key), so seven answered and Continue enabled are never out of
  // step. It is a count, not a verdict: nothing here says pass or fail.
  const answeredCount = PARQ_QUESTIONS.filter((q) => typeof parq[q.key] === 'boolean').length;

  // B2 (COPPA, founder-ratified 2026-07-04) — birth month + year are
  // REQUIRED: age drives the clinical bands and the under-13 consent
  // gate, and an optional field is a standing bypass of both. Month +
  // year only (never full DOB) — exact enough at the 13 boundary,
  // minimal beyond it.
  const thisYear = new Date().getFullYear();
  const yearOptions: number[] = [];
  for (let y = thisYear - 5; y >= thisYear - 99; y--) yearOptions.push(y);
  // B123 (2026-09-21) — a hydrated blob's birthMonth/birthYear can hold a
  // non-number (an old or hand-edited blob). Reading only a genuine `number`
  // — never coercing a numeric-looking string — keeps the value shown in the
  // selects below and the birthDone gate in lockstep: a non-number is
  // absent for BOTH, so the row renders empty rather than a "phantom pick"
  // (a select that LOOKS filled while Continue stays disabled with nothing
  // on screen explaining why).
  const birthMonth = typeof answers.birthMonth === 'number' ? answers.birthMonth : undefined;
  const birthYear = typeof answers.birthYear === 'number' ? answers.birthYear : undefined;
  const birthDone = typeof birthMonth === 'number' && typeof birthYear === 'number';
  // Fix round 1 (critic LOW 2) — the birth sentence can only ever be shown on a screen that
  // opened with the birth row, so only that screen reserves its room under the docked bar.
  // Read once, at mount: the row going away must not shrink the bar.
  const [birthAskedAtMount] = useState(!birthDone);

  return (
    <>
      <StepHeading>Quick health check</StepHeading>
      <StepSub>
        These are standard pre-exercise screening questions. Required by
        responsible coaching practice before starting a program.
      </StepSub>

      {arrivedFromReveal && (
        <p className={styles.arrival}>
          Your dashboard is ready once these seven answers are in.
        </p>
      )}

      {/* ⚖️ REL-29 — the deterministic age step at the B26 seam asks this
          FIRST, and writes the same two answer keys. Once they are present this
          row hides, so nobody is asked their birthday twice. It still renders
          for anyone who reached step 3 without passing the new step (a hydrated
          mid-flow blob, a `?step=3` preview link), and the CTA below still
          refuses to continue without it — the B2 requirement is unchanged. */}
      {!birthDone && (
      <div className={styles.birthRow} role="group" aria-label="Birth month and year">
        <div className={styles.birthLabel}>
          When were you born?
          <span className={styles.birthHint}>
            Month and year only. It sets your age-appropriate training limits.
          </span>
        </div>
        <div className={styles.birthSelects}>
          <select
            className={styles.birthSelect}
            aria-label="Birth month"
            value={birthMonth ?? ''}
            onChange={(e) =>
              setAnswer({ birthMonth: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value="" disabled>
              Month
            </option>
            {BIRTH_MONTHS.map((m, i) => (
              <option key={m} value={i + 1}>
                {m}
              </option>
            ))}
          </select>
          <select
            className={styles.birthSelect}
            aria-label="Birth year"
            value={birthYear ?? ''}
            onChange={(e) =>
              setAnswer({ birthYear: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value="" disabled>
              Year
            </option>
            {yearOptions.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>
      </div>
      )}

      <div className={styles.introBlock}>
        <p className={styles.intro}>
          Answer all 7. Your coaches use the result to set your starting intensity.
        </p>
        {/* Round-29 critic 4 — THE REGION IS ALWAYS MOUNTED, empty until there
            is something to count. A live region inserted together with its
            first text is not announced by most screen readers, so the first
            answer passed in silence.

            Round-31 UX polish 1 — an empty <p> has no children and would
            draw no line box, so `.count` RESERVES one unconditionally (a 4px
            margin plus a 1.5em min-height = 23.5px). The space is held from
            the first render, so the first answer changes what this line SAYS
            and moves nothing under it; the price is that the screen before any
            answer is 23.5px taller than it was before the reserve. The
            `.count:not(:empty)` guard that made the empty state free was
            deleted by that same change.

            Round-37 critic F2 — the one state the one-line reserve does NOT
            cover is the missing-birth hint below: it is a second line box in
            the same region, so the seventh answer grows this block by 19.5px.
            The CSS carries the arithmetic.

            Round-29 critic 2 — and the missing birth row is named INSIDE this
            region, so it is announced with the seventh answer rather than
            appearing in silence beside it. */}
        <p className={styles.count} role="status">
          {answeredCount > 0 ? `${answeredCount} of 7 answered` : null}
          {allDone && !birthDone ? (
            <>
              {' '}
              <span className={styles.countHint}>{BIRTH_REQUIRED_HINT}</span>
            </>
          ) : null}
        </p>
      </div>

      <div className={styles.questions} role="group" aria-label="Health screening questions">
        {PARQ_QUESTIONS.flatMap((q) => {
          const value = parq[q.key];
          const row = (
            <div key={q.key} className={styles.question}>
              <div className={styles.questionText}>{q.question}</div>
              <div className={styles.btnRow}>
                <button
                  type="button"
                  className={[
                    styles.ynBtn,
                    value === false && styles.selectedNo,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={value === false}
                  onClick={() => setAnswerForKey(q.key, false)}
                >
                  No
                </button>
                <button
                  type="button"
                  className={[
                    styles.ynBtn,
                    value === true && styles.selectedYes,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  aria-pressed={value === true}
                  onClick={() => setAnswerForKey(q.key, true)}
                >
                  Yes
                </button>
              </div>
            </div>
          );
          if (q.key !== firstYesKey) return [row];
          return [
            row,
            <div key="parq-caution" className={styles.warning} role="alert">
              <svg
                className={styles.warningIcon}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M12 3l10 18H2L12 3z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 10v5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                />
                <circle cx="12" cy="18" r="0.9" fill="currentColor" />
              </svg>
              <div>
                We recommend checking with a healthcare provider before
                starting an exercise program. Your coaches will note this and
                start you at a conservative intensity.
              </div>
            </div>,
          ];
        })}
      </div>

      <StepCta onClick={onContinue} disabled={!allDone || !birthDone}>
        Continue
      </StepCta>
      {/* B465 — a disabled Continue used to give no reason beside it; the counting
          line above names the missing half, but a person looking at the button saw
          grey and nothing else. */}
      {/* B471 (the B460 rule) — the line's room is always reserved. It used to be
          unmounted when the seventh answer landed, the page below Continue lost about
          27 px, and a tap on Continue at its old place missed. The paragraph stays
          mounted and only stops saying anything; the stylesheet holds its one line.
          Fix round 1 (critic LOW 2) — the same under the docked bar (Part 7): BarNote
          stays mounted with empty children and `reserve` names the sentences this screen
          can show, so the bar holds the tallest and nothing moves when the line goes quiet. */}
      <BarNote
        className={styles.reason}
        reserve={birthAskedAtMount ? [QUESTIONS_REQUIRED_HINT, BIRTH_REQUIRED_HINT] : [QUESTIONS_REQUIRED_HINT]}
      >
        {!allDone ? QUESTIONS_REQUIRED_HINT : !birthDone ? BIRTH_REQUIRED_HINT : null}
      </BarNote>
    </>
  );
}
